/**
 * Products translations: domains, domain, tldCatalog, cart, forms
 */

import { VSERVER_SMTP_BLOCK_DATE } from '@/legal/vserverSmtpBlockDate'

export const domains = {
  check: {
    eyebrow: 'Domain registration',
    title: 'Your domain, wired into your whole stack',
    subtitle: 'Search 500+ TLDs at transparent per-TLD prices — then point your new domain at your hosting, mail or Kubernetes ingress from the same panel. No nameserver copy-paste.',
    toCart: 'Go to cart',
    searchAriaLabel: 'Domain search',
    popular: {
      title: 'Popular domain extensions',
      subtitle:
        'Prices per year, VAT included. Registration, renewal and transfer prices for all 500+ extensions are in the TLD overview.',
      cta: 'See all extensions and prices'
    },
    included: {
      title: 'Included with every domain',
      dnsTitle: 'DNS management in the panel',
      dnsDesc: 'edit records without filing a ticket',
      dyndnsTitle: 'Wildcard DynDNS included',
      dyndnsDesc: 'one entry for every subdomain',
      instantTitle: 'Instant registration',
      instantDesc: 'live within minutes for most extensions',
      integratedTitle: 'Wired in',
      integratedDesc: 'hosting, mail or Kubernetes ingress, all in the same panel',
      addon:
        'Optional add-on for supported extensions: WHOIS protection — hides your personal data from public WHOIS queries.'
    },
    transfer: {
      title: 'Domain already registered somewhere else?',
      body: 'Move it over with your auth code and manage the domain, DNS and renewals in the same panel as the rest of your stack. Per-TLD transfer prices are in the TLD overview.',
      cta: 'Start a transfer'
    }
  },
  search: {
    singlePlaceholder: 'Enter your domain (e.g. example.com or example.eu)',
    singlePlaceholderShort: 'Enter your domain',
    multiPlaceholder: 'Enter domains (one per line)\ne.g. example.eu\nexample.com:AUTHCODE',
    multiHint: 'Supported: line break, comma, semicolon · Max. {max}',
    statsDetected: 'Detected: {count}',
    statsInvalid: 'Invalid: {count}',
    statsDuplicates: 'Duplicates removed: {count}',
    statsLimitReached: 'Limit reached ({max})',
    multicheck: 'Multicheck',
    uploadFile: 'Upload file',
    allTlds: 'All TLDs',
    format: 'Format',
    clear: 'Clear',
    checkDomain: 'Check domain'
  },
  trust: {
    nis2: {
      title: 'Verified contact data',
      subtitle: 'per NIS2 Art. 28'
    },
    eu: {
      title: 'EU-based registrar',
      subtitle: 'Domains managed in the EU'
    }
  },
  band: {
    priceFrom: 'from',
    perYear: '/year',
    checkDomain: 'Check domain',
    enterDomain: 'Enter domain name',
    selectDomain: 'Select .{tld} domain, from {price} per year',
    defaultDomain: 'mydomain'
  },
  tlds: {
    de: 'Germany',
    com: 'International',
    eu: 'Europe',
    at: 'Austria',
    ch: 'Switzerland',
    net: 'Network',
    germany: 'Germany',
    international: 'International',
    europe: 'Europe',
    austria: 'Austria',
    switzerland: 'Switzerland',
    network: 'Network'
  },
  results: {
    filters: {
      freeOnly: 'Available only',
      allExtensions: 'All extensions',
      extension: 'Extension',
      sort: 'Sort',
      relevance: 'Relevance',
      priceAsc: 'Price ascending',
      priceDesc: 'Price descending',
      extensionAZ: 'Extension A–Z'
    },
    selection: {
      hits: '{count} results',
      selected: '{count} selected',
      selectAllVisible: 'Select all visible',
      clearSelection: 'Clear selection',
      registerCount: 'Register {count}',
      freeDomainsSelected: '{count} available domains selected',
      register: 'Register'
    },
    categories: {
      yourDomain: 'Your desired domain',
      searchResults: 'Search results',
      domainHacks: 'Domain hacks',
      creativeAlternatives: 'Creative alternatives',
      popularExtensions: 'Popular extensions',
      additionalFreeExtensions: 'Additional available extensions'
    },
    moreFree: {
      searchMore: 'Search for more available extensions',
      allChecked: 'All {count} TLDs have been checked'
    },
    checkingMore: 'Checking more extensions …',
    tableHeaders: {
      domain: 'Domain',
      status: 'Status',
      price: 'Price',
      authToken: 'Auth token',
      action: 'Action'
    },
    addedToCart: 'Added to cart',
    addedToCartMessage: '{count} domain has been added to the cart | {count} domains have been added to the cart',
    pricing: {
      inclVat: 'all prices incl. VAT',
      exclVat: 'all prices excl. VAT'
    },
    idn: {
      invalidChars: 'Invalid characters for .{tld}',
      invalidCharsDetail: 'The characters "{chars}" are not allowed for .{tld} domains.',
      notSupported: '.{tld} does not support special characters',
      allowedChars: 'Allowed special characters: {chars}',
      skippedDomains: '{count} domain with invalid characters skipped | {count} domains with invalid characters skipped',
      filteredHint: 'Some extensions do not support special characters and may not be displayed.'
    },
    validation: {
      invalidTitle: 'Invalid domain',
      invalidMessage: 'Please enter a valid domain name, e.g. "example" or "example.com".',
      noResultsTitle: 'No results',
      noResultsMessage: 'No domains were found for your search. Please try a different name.'
    }
  }
} as const

export const domain = {
  config: {
    title: 'Configure domain',
    perYear: 'year',
    year: 'year',
    contacts: 'Contacts',
    contactsHint: 'Optional: Select owner and admin contact for this domain.',
    owner: 'Owner',
    admin: 'Admin',
    selectHandle: 'Select contact...',
    addons: 'Additional options',
    whoisProtection: 'WHOIS protection',
    whoisProtectionDesc: 'Hides your personal data in public WHOIS queries.',
    trusteeService: 'Trustee service',
    trusteeDesc: 'This TLD requires an admin contact with residence in the target country. The trustee will fulfil this function for you.',
    viewContract: 'View trustee contract',
    total: 'Total',
    addToCart: 'Add to cart',
    alreadyInCart: 'Already in cart',
    alreadyInCartMessage: '{domain} is already in the cart.',
    addedToCart: 'Added',
    addedToCartMessage: '{domain} has been added to the cart.'
  }
} as const

export const tldCatalog = {
  eyebrow: 'Domain extensions',
  title: 'TLD overview',
  subtitle: 'All available domain extensions with current prices. Choose your TLDs and check the availability of your desired domain.',
  instantRegistration: 'Instant registration',
  whoisProtection: 'WHOIS protection optional',
  loading: 'Loading TLDs...',
  loadMore: 'Load more TLDs',
  categories: {
    all: 'All categories',
    generic: 'Generic TLDs',
    cctld: 'Country code TLDs (ccTLDs)',
    newGtld: 'New gTLDs',
    other: 'Other'
  },
  filters: {
    category: 'Category',
    search: 'Search TLD',
    searchPlaceholder: 'e.g. co.uk, com, london...',
    promoOnly: 'Promotional TLDs only',
    maxPrice: 'Max. price',
    noLimit: 'All',
    showing: '{count} of {total} TLDs'
  },
  pricing: {
    registration: 'Registration',
    renewal: 'Renewal',
    transfer: 'Transfer'
  },
  selection: {
    count: '{count} of {max} TLDs selected',
    placeholder: 'Enter domain name',
    check: 'Check',
    clear: 'Clear selection'
  },
  empty: {
    title: 'No TLDs found',
    description: 'Try a different search or select a different category.'
  },
  errors: {
    loadFailed: 'Failed to load TLDs.',
    invalidData: 'Invalid data from server.'
  }
} as const

export const cart = {
  title: 'Shopping cart',
  itemCount: '{count} items',
  loading: 'Loading cart...',
  saveError: {
    title: 'Error',
    message: 'Could not save your cart. Please try again.'
  },
  mailErrors: {
    alreadySubscribed: 'This domain already has email hosting, or it is already in your cart.',
    includedInHosting: 'Email for this domain is already included in your hosting package. There is nothing to book.',
    adminHold: 'We have blocked email for this domain, so it cannot be booked. Please contact support.',
    domainSuspended: 'The domain is suspended. You can book email hosting once the suspension is lifted.',
    domainDeleted: 'The domain was deleted. Restore it first, then you can book email hosting.',
    heldElsewhere: 'This domain belongs to another customer account here. Only that account can book email hosting for it.',
    domainUnavailable:
      "You cannot book email hosting for this name here: it belongs to another account's domain or is not a domain of its own. If it is your domain, please contact support."
  },
  categories: {
    webhosting: 'Web hosting',
    domains: 'Domains',
    kubernetes: 'Kubernetes',
    vserver: 'VServer',
    email: 'Email Hosting',
    other: 'Other'
  },
  item: {
    paymentInterval: 'Payment interval',
    pricePerMonth: 'Price per month',
    pricePerQuarter: 'Price per quarter',
    pricePerHalfYear: 'Price per half-year',
    pricePerYear: 'Annual price',
    total: 'Total',
    oneTime: 'One-time',
    unitPrice: 'Unit price',
    price: 'Price',
    pricePending: 'calculating',
    remove: 'Remove'
  },
  summary: {
    title: 'Summary',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    halfYearly: 'Half-yearly',
    yearly: 'Yearly',
    net: 'Net',
    vat: 'VAT {rate}%',
    dueTodayGross: 'Due today (Gross)',
    dueToday: 'Due today',
    pricesIncludeTax: 'All prices incl. VAT.',
    pricesExcludeTax: 'All prices excl. VAT.',
    reverseChargeNote: 'Net prices. Reverse charge: you account for the VAT in your own country.',
    notTaxableNote: 'Net prices. Not taxable in Germany, so no VAT is charged.',
    vatExemptNotice: 'VAT not applicable (small business exemption)',
    acceptTerms: 'I accept the',
    terms: 'Terms and Conditions',
    /**
     * § 356(5) BGB lets the right of withdrawal lapse on complete performance
     * only after two declarations: an express request for early performance
     * and confirmed awareness of the lapse. No compensation for value is
     * claimed on withdrawal (GTC § 12.5), so the text announces none.
     */
    withdrawalConsent:
      'I expressly request that you begin performing the service before the withdrawal period expires. I am aware that my right of withdrawal expires once you have performed the service in full.',
    withdrawalConsentDomains:
      'I am aware that registering a domain is completely performed upon entry in the registry, and that my right of withdrawal expires in that respect upon registration.',
    withdrawalPolicy: 'withdrawal instructions',
    mailSendLimitNote:
      'Email hosting: for the first 30 days from registration of the customer account, reduced sending limits of 30 messages per hour and 100 per day per mailbox apply. Messages beyond that are not lost but are temporarily rejected and stay in the email client; depending on the client, delivery is retried automatically. The regular limits apply thereafter. Details in § 5 of the Email Hosting Service Agreement.',
    termContract: 'Contract term',
    termIndefinite: 'Indefinite term, no minimum term. Terminable at any time; the termination takes effect one month after receipt, for a VServer on receipt. Time paid in advance beyond that is refunded pro rata.',
    termDomain: 'Indefinite term, terminable at any time with effect one month after receipt, for a VServer on receipt. We buy the registration period (usually 12 months) from the registry in advance; its fee is not refunded and the domain stays registered until the period ends.',
    termNoticeLabel: 'Termination',
    termNotice: 'At any time via "Verträge hier kündigen" or the customer portal',
    submitOrder: 'Place binding order'
  },
  checkout: {
    acceptTerms: 'I accept the',
    terms: 'Terms and Conditions',
    withdrawalConsent:
      'I expressly request that you begin performing the service before the withdrawal period expires. I am aware that my right of withdrawal expires once you have performed the service in full.',
    withdrawalConsentDomains:
      'I am aware that registering a domain is completely performed upon entry in the registry, and that my right of withdrawal expires in that respect upon registration.',
    withdrawalPolicy: 'withdrawal instructions',
    mailSendLimitNote:
      'Email hosting: for the first 30 days from registration of the customer account, reduced sending limits of 30 messages per hour and 100 per day per mailbox apply. Messages beyond that are not lost but are temporarily rejected and stay in the email client; depending on the client, delivery is retried automatically. The regular limits apply thereafter. Details in § 5 of the Email Hosting Service Agreement.',
    termContract: 'Contract term',
    termIndefinite: 'Indefinite term, no minimum term. Terminable at any time; the termination takes effect one month after receipt, for a VServer on receipt. Time paid in advance beyond that is refunded pro rata.',
    termDomain: 'Indefinite term, terminable at any time with effect one month after receipt, for a VServer on receipt. We buy the registration period (usually 12 months) from the registry in advance; its fee is not refunded and the domain stays registered until the period ends.',
    termNoticeLabel: 'Termination',
    termNotice: 'At any time via "Verträge hier kündigen" or the customer portal',
    submitOrder: 'Place binding order',
    paymentsDisabled: 'Online payments are currently unavailable. Please try again later.',
    loginRequired: 'Please log in or register to continue.',
    newRegistration: 'Register now',
    or: 'or',
    errors: {
      cartPaid: 'This cart has already been paid. You will find the order under "Purchases".',
      cartChanged: 'The cart changed while you were paying. Please review it and try again.',
      cartBusy: 'A payment is already running for this cart. Please try again in a moment.',
      productUnavailable: 'A product in your cart cannot be ordered. Please remove it from your cart.',
      periodUnavailable: 'This billing period is not available for email hosting. Reload the page and try again.',
      threeDSFailed: '3DS authentication failed',
      cardDeclined: 'Your card was declined. Please try a different payment method.',
      insufficientFunds: 'Your card has insufficient funds. Please try a different payment method.',
      expiredCard: 'Your card has expired. Please update your payment method.',
      paymentDeclined: 'The payment was declined. Please try a different payment method.',
      paymentMethodRequired: 'This order needs a saved payment method. Please add one to your account.',
      paymentSetupUnavailable: 'The payment could not be prepared just now. Nothing was charged. Please try again in a moment.',
      vserverTemplateUnavailable: 'The operating system chosen for your VServer is no longer available. Please choose another.',
      vserverHostnameInvalid: 'Your VServer hostname is invalid. Use letters, digits, hyphens and dots, at most 63 characters.',
      cartOwnerRequired: 'This cart belongs to another account. Please log in with that account or start a new cart.',
      cartTooLarge: 'Your cart has too many lines. Please split the order.',
      termsReload: 'Reload page',
      termsVersionStale: 'Our terms have been updated since. Reload the page, read the current version and confirm it again.',
      consentRequired: 'Please confirm the terms and the withdrawal policy again for the cart as it stands.',
      consentVersionRequired: 'Your order is missing the version of the terms. Please reload the page and try again.',
      avvAcceptanceRequired: 'For this order you first need to accept the data processing agreement (AVV) in your account.'
    }
  },
  validation: {
    title: 'Please fix the following errors',
    validating: 'Validating cart...',
    errors: {
      invalid_domain: 'Invalid domain name',
      incompatible_account_type: 'Account type not compatible with this TLD',
      missing_handle: 'Contact required for domain',
      handle_not_verified: 'A new registration needs a contact with a verified email address and phone number.',
      invalid_handle: 'The selected contact cannot be used for this domain. Please choose another.',
      wrong_handle_type: 'The selected contact type is not allowed for this domain ending.',
      idn_invalid: 'The registry does not accept the characters in this domain name.',
      authcode_required: 'Transferring this domain needs the auth code from your current provider. Take the domain out of the cart and add it again from the domain search with the code.',
      internal_transfer_authcode_required: 'Transferring this domain needs its auth code from the current holder. Take the domain out of the cart and add it again from the domain search with the code.',
      domain_conflict: 'This domain is already taken here and cannot be ordered for your account.'
    }
  },
  empty: {
    title: 'Your cart is empty',
    description: 'Add products to start your order.',
    subtitle: 'Discover our products and find the right offer for you.',
    browseDomains: 'Search domains',
    browseProducts: 'Discover products',
    action: 'Discover products'
  },
  payment: {
    title: 'Complete payment',
    amountLabel: 'Amount to pay',
    loading: 'Loading payment form...',
    validationError: 'Validation error',
    paymentFailed: 'Payment failed',
    unknownError: 'Unknown error',
    securityNote: 'Secure payment. Your card details are not stored.',
    payAmount: 'Pay {amount}'
  },
  finalizing: {
    processing: {
      title: 'Processing order...',
      subtitle: 'Please wait a moment.',
      checkingStatus: 'Checking status...',
      processing_long: 'Your payment is being processed. This may take a few seconds.'
    },
    success: {
      title: 'Payment received!',
      subtitle: 'Your order is now being processed. You will receive an email once everything is ready.',
      redirecting: 'Redirecting in {seconds} seconds...',
      viewOrders: 'View orders',
      toDashboard: 'Go to dashboard',
      viewReceipt: 'View receipt',
      thankYou: 'Thank you for your order!',
      mailDnsHint:
        'Email for {domain} only starts arriving once the DNS records are in place.',
      setupMailDns: 'Set up email DNS'
    },
    error: {
      title: 'An error occurred',
      defaultMessage: 'Please try again or contact support.',
      paymentFailed: 'The payment could not be completed. Please try again.',
      paymentMethodRejected: 'The payment method was rejected. Please choose a different payment method.',
      backToCart: 'Back to cart',
      toDashboard: 'Go to dashboard'
    },
    help: {
      text: 'If you have any questions, please contact our support at',
      email: "support{'@'}itsh.de"
    }
  },
  warnings: {
    paymentsDisabled: 'Online payments are currently unavailable.',
    orphanedItems: '{count} item could not be loaded. | {count} items could not be loaded.',
    removeOrphaned: 'Remove'
  },
  phoneVerification: {
    title: 'Verify phone number',
    subtitle: 'Please verify your phone number to proceed.',
    sendCode: 'Send code',
    enterCode: 'Enter code',
    confirm: 'Confirm',
    resend: 'Resend',
    error: 'Could not send code. Please try again.',
    invalidCode: 'Please enter a 6-digit code.',
    wrongCode: 'Invalid code. Please try again.',
    phoneInUse: 'This phone number is already verified on another account. Please use your own number or contact support.',
    tooManyRequests: 'Too many codes requested. Please try again in an hour.',
    phoneNotMobile: 'Codes can only be sent by SMS to mobile numbers. A number outside the EEA, Switzerland and the UK has to be in your own country. Change it in your account settings.',
    smsUnavailable: 'Verification by SMS is not available right now. Please try again tomorrow.',
    tooManyFailures: 'Too many wrong codes. Please try again tomorrow.',
    smsDailyLimit: 'Your account has used today\'s verification texts. Please try again tomorrow.',
    contactChanged: 'Your contact details are still being updated. Save your account details again, then retry.',
    serviceUnavailable: 'Verification is not available right now. Please try again in a few minutes.'
  },
  handleWarning: {
    title: 'Verified contact required',
    subtitle: 'For domain registration, email and phone must be verified (NIS2).',
    message: 'A verified contact is required for domain registration.',
    action: 'Verify contacts',
    emailVerified: 'Email verified',
    emailMissing: 'Email not verified',
    phoneVerified: 'Phone verified',
    phoneMissing: 'Phone not verified',
    deadlineExpired: 'Verification deadline expired',
    deadlineApproaching: 'Verification deadline approaching',
    deadlineExpiredMessage: 'The verification deadline for these contacts has expired. Domain registrations with these contacts are not possible.',
    expired: 'Expired',
    daysRemaining: '{days} days remaining'
  },
  secureOnlyNotice: {
    title: 'HTTPS required',
    message: 'The following domains use a secure TLD and only work with HTTPS/SSL:'
  },
  sshKey: {
    title: 'SSH key',
    subtitle: 'Choose the SSH key you will use to log in to your server. Optional — you can also add access after purchase.',
    addNew: 'New key',
    add: 'Add SSH key',
    empty: 'You have no SSH key on file yet. No problem — you can also set up access from the dashboard after purchase.',
    loading: 'Loading SSH keys...',
    hint: 'Without a key your server starts without access — you then inject a key or set a root password from the dashboard.'
  },
  vserverSmtpNotice: `If this is your first VServer and you order it on or after ${VSERVER_SMTP_BLOCK_DATE.en}, outbound port 25 (delivery to other mail servers) is blocked for three months. Mail over the submission ports 587 and 465 always works.`,
  errors: {
    verificationRequired: {
      title: 'Contact verification required',
      message: 'A fully verified contact is required for this order. Please verify your contact\'s email and phone.'
    },
    deadlineExpired: {
      title: 'Verification deadline expired',
      message: 'The verification deadline for the contact used has expired. Please update and verify your contact details again.'
    },
    checkout: {
      title: 'Order failed',
      message: 'An error occurred during checkout. Please try again later.'
    }
  },
  emailVerification: {
    title: 'Email confirmation required',
    message: 'We have sent a confirmation link to {email}.',
    hint: 'Please check your inbox and click the link.'
  },
  handleSelector: {
    label: 'Owner contact',
    placeholder: 'Select...',
    owner: 'Owner',
    admin: 'Admin',
    noCompatibleHandles: 'No compatible contact available',
    errors: {
      requiresOrg: 'Organisation',
      requiresPerson: 'Individual',
      incompatibleType: '{tld} requires {required}',
      requiresLocal: '{tld} requires contact from {country}',
      companyNeeded: '{tld} requires a company contact',
      personNeeded: '{tld} requires an individual'
    }
  },
  pageTitle: 'Shopping cart',
  pageSubtitle: '{count} items',
  items: 'Items'
} as const

export const forms = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email address',
  phone: 'Phone',
  street: 'Street',
  addressSupplement: 'Address supplement',
  postcode: 'Postcode',
  city: 'City',
  state: 'County/Region',
  country: 'Country',
  organization: 'Organisation',
  vatId: 'VAT number',
  iban: 'IBAN',
  bankData: 'Bank details',
  ibanPlaceholder: 'GB00 XXXX 0000 0000 0000 00'
} as const
